// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Pure node-ordering transforms — no DOM, no render imports.
//
// Like reroot.js, ladderise emits BRAND-NEW raw nodes ({ name?, branchLength,
// children? }) so the result feeds straight back through buildGraph's
// object-source path. It never recycles annotated graph nodes — traverseTree
// mutates whatever it is handed, so reusing them would corrupt the source.
//
// Sort key is each child's subtree leaf count (totalLeaves), with the longest
// branch (totalSubtreeLength) breaking ties so the ordering is deterministic.

/**
 * Ladderise: reorder every internal node's children by subtree size so the tree
 * reads as a clean "ladder". Distance-preserving (only sibling order changes).
 *
 * @param {object} graph - buildGraph output (uses graph.preorderTraversal).
 * @param {"ascending"|"descending"} [direction] - small-to-large (default) or
 *   large-to-small at every node.
 * @returns {object} a fresh raw root node for buildGraph's object-source path.
 */
export function ladderise(graph, direction = "ascending") {
  const sign = direction === "descending" ? -1 : 1;

  function emit(node) {
    const fresh = { branchLength: node.branchLength };
    if (node.isLeaf) {
      fresh.name = node.id; // leaf identity survives the re-parse via its id
      return fresh;
    }
    if (node.name != null) fresh.name = node.name; // preserve clade labels

    const sorted = [...node.children].sort((a, b) => {
      const bySize = a.totalLeaves - b.totalLeaves;
      if (bySize !== 0) return sign * bySize;
      return sign * (a.totalSubtreeLength - b.totalSubtreeLength);
    });
    fresh.children = sorted.map(emit);
    return fresh;
  }

  return emit(graph.preorderTraversal[0]);
}
